import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, doc, getDocs, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { db } from "./firebase";
import { EmptyState, PageHeader, StatCard, ToneBadge } from "./petaarUi";

const maskOwner = (email) => {
  const value = String(email || "").trim();
  if (!value.includes("@")) return "Pet Owner";
  const [local] = value.split("@");
  if (!local) return "Pet Owner";
  return `${local.slice(0, 1)}${"*".repeat(Math.max(2, local.length - 1))}`;
};

function ratingTone(rating) {
  if (rating >= 4.5) return "success";
  if (rating >= 3) return "accent";
  return "warning";
}

export default function ProviderReviews({ user }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [ratingFilter, setRatingFilter] = useState("");
  const [search, setSearch] = useState("");
  const [replyDrafts, setReplyDrafts] = useState({});
  const [savingReplyId, setSavingReplyId] = useState("");
  const providerEmail = String(user?.email || "").trim();
  const providerEmailVariants = useMemo(
    () => [...new Set([providerEmail, providerEmail.toLowerCase()].filter(Boolean))],
    [providerEmail],
  );

  const loadReviews = useCallback(async () => {
    if (!providerEmailVariants.length) {
      setReviews([]);
      return;
    }
    try {
      setLoading(true);
      setLoadError("");
      const snapshots = await Promise.all(
        providerEmailVariants.map((emailVariant) =>
          getDocs(query(collection(db, "reviews"), where("providerEmail", "==", emailVariant))),
        ),
      );
      const merged = new Map();
      snapshots.forEach((snap) => {
        snap.docs.forEach((item) => {
          const review = { id: item.id, ...item.data() };
          if (review.hidden === true) return;
          merged.set(item.id, review);
        });
      });
      const list = [...merged.values()];
      list.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
      setReviews(list);
      setReplyDrafts((prev) => {
        const next = { ...prev };
        list.forEach((review) => {
          if (next[review.id] === undefined) {
            next[review.id] = String(review.providerReply || "");
          }
        });
        return next;
      });
    } catch (error) {
      setLoadError(error.message || "Unable to load reviews.");
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [providerEmailVariants]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const summary = useMemo(() => {
    const valid = reviews.map((review) => Number(review.rating)).filter((rating) => Number.isFinite(rating) && rating >= 1 && rating <= 5);
    const count = valid.length;
    const avg = count ? valid.reduce((sum, rating) => sum + rating, 0) / count : 0;
    const withReply = reviews.filter((review) => String(review.providerReply || "").trim()).length;
    const recentLow = reviews.filter((review) => Number(review.rating) <= 2).length;
    return { count, avg, withReply, recentLow };
  }, [reviews]);

  const filtered = useMemo(() => {
    return reviews
      .filter((review) => {
        const rating = Number(review.rating);
        return ratingFilter ? rating >= Number(ratingFilter) : true;
      })
      .filter((review) => {
        const q = String(search || "").trim().toLowerCase();
        if (!q) return true;
        return (
          String(review.comment || "").toLowerCase().includes(q) ||
          String(review.serviceTitle || "").toLowerCase().includes(q) ||
          String(review.bookingId || "").toLowerCase().includes(q)
        );
      });
  }, [reviews, ratingFilter, search]);

  const saveReply = async (review) => {
    const text = String(replyDrafts[review.id] || "").trim();
    if (!text) {
      alert("Reply cannot be empty.");
      return;
    }
    if (text.length > 1000) {
      alert("Reply must be under 1000 characters.");
      return;
    }
    try {
      setSavingReplyId(review.id);
      await updateDoc(doc(db, "reviews", review.id), {
        providerReply: text,
        providerRepliedAt: review.providerRepliedAt || serverTimestamp(),
        providerReplyUpdatedAt: serverTimestamp(),
      });
      await loadReviews();
      alert("Reply saved.");
    } catch (error) {
      alert(error.message || "Unable to save reply.");
    } finally {
      setSavingReplyId("");
    }
  };

  return (
    <div className="petaar-page">
      <PageHeader
        eyebrow="Provider Reputation"
        title="Customer Reviews"
        subtitle="Track ratings, search sentiment, and reply professionally from a single moderated inbox."
        actions={
          <button className="petaar-button" onClick={loadReviews} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        }
      />

      {loadError ? <div className="petaar-notice error" style={{ marginBottom: 16 }}>{loadError}</div> : null}

      <div className="petaar-stat-grid" style={{ marginBottom: 24 }}>
        <StatCard label="Average rating" value={summary.avg} decimals={1} suffix="/5" tone="accent" meta={`${summary.count} total reviews`} />
        <StatCard label="Reviews replied" value={summary.withReply} tone="success" meta="Provider responses posted" />
        <StatCard label="Low-rating reviews" value={summary.recentLow} tone="warning" meta="Ratings of 2 or below" />
        <StatCard label="Visible reviews" value={filtered.length} tone="default" meta="Current search result" />
      </div>

      <section className="petaar-card" style={{ marginBottom: 24 }}>
        <div className="petaar-section-title">
          <div>
            <div className="petaar-kicker">Filters</div>
            <h3 style={{ fontSize: "1.35rem" }}>Search and segment feedback</h3>
          </div>
          <button
            className="petaar-button petaar-button--ghost"
            onClick={() => {
              setSearch("");
              setRatingFilter("");
            }}
          >
            Clear Filters
          </button>
        </div>
        <div className="petaar-form-grid">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by service title, review text, or booking id"
          />
          <select value={ratingFilter} onChange={(e) => setRatingFilter(e.target.value)}>
            <option value="">All ratings</option>
            <option value="4">4+ stars</option>
            <option value="3">3+ stars</option>
            <option value="2">2+ stars</option>
          </select>
        </div>
      </section>

      {loading ? (
        <div className="petaar-grid" style={{ gap: 16 }}>
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={`review-skeleton-${index}`} className="petaar-skeleton" style={{ height: 240, borderRadius: 18 }} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="⭐"
          title="No reviews found"
          description="Reviews will appear here after customers complete bookings and share their experience."
        />
      ) : (
        <div className="petaar-grid" style={{ gap: 18 }}>
          {filtered.map((review) => {
            const rating = Number(review.rating || 0);
            return (
              <section key={review.id} className="petaar-card" style={{ display: "grid", gap: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                  <div>
                    <h3 style={{ fontSize: "1.25rem" }}>{review.serviceTitle || "Service"}</h3>
                    <div style={{ marginTop: 6, color: "var(--muted)" }}>
                      Reviewer: {maskOwner(review.ownerEmail)} · Booking: {review.bookingId || "N/A"} · Date: {review.bookingDate || "N/A"}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <ToneBadge tone={ratingTone(rating)}>{rating.toFixed(1)}/5</ToneBadge>
                    {review.verifiedBooking ? <ToneBadge tone="success">Verified booking</ToneBadge> : null}
                  </div>
                </div>

                <div style={{ color: "var(--text)", lineHeight: 1.7 }}>
                  {review.comment || "No comment"}
                </div>

                {review.providerReply ? (
                  <div className="petaar-notice info">
                    <strong>Your Reply</strong>
                    <div style={{ marginTop: 8 }}>{review.providerReply}</div>
                  </div>
                ) : null}

                <div style={{ display: "grid", gap: 10 }}>
                  <textarea
                    value={replyDrafts[review.id] || ""}
                    onChange={(e) => setReplyDrafts((prev) => ({ ...prev, [review.id]: e.target.value }))}
                    placeholder="Write a professional response to this review"
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <small style={{ color: "var(--muted)" }}>
                      Keep replies concise, factual, and helpful. Personal contact details should not be shared.
                    </small>
                    <button className="petaar-button" onClick={() => saveReply(review)} disabled={savingReplyId === review.id}>
                      {savingReplyId === review.id ? "Saving..." : (review.providerReply ? "Update Reply" : "Post Reply")}
                    </button>
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
